import { CustomItemDataType, DonationFundPlatformTypeEnum } from '@graphql/generated-base';
import { useTranslation } from '@shared/core';
import { useCallback } from 'react';
import { PLATFORM_LABELS } from '../../CheckoutDialog.constants';
import { useCheckoutFunnel } from '../../hooks/useCheckoutFunnel';
import { useCheckoutInitDataContext } from '../../hooks/useCheckoutInitData';
import { useCheckoutTelemetryData, useGetProductData } from '@apps/registry/guest/components/CheckoutDialog/hooks';
import { withWindow } from '@shared/utils/withWindow';
import { runIfFn } from '@shared/utils/functions';
import { addAction } from '@shared/utils/logger';
import { useShoppingCartTelemetry } from '@apps/registry/common/components/ShoppingCart/ShoppingCart.telemetry';
import { useFormatRedirectURL } from '@shared/utils/formatting/redirectURL.formatter';
import { isAffiliateWrappableItemType } from '@apps/registry/common/util/storeHelpers';
import { useGuestRegistryTelemetry } from '@apps/registry/guest/GuestRegistry.telemetry';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';

const VENMO_PAYMENT_URL = 'https://account.venmo.com/payment-link';

function buildVenmoPaymentLink(username: string, amount: string, note?: string) {
  const url = new URL(VENMO_PAYMENT_URL);

  const params = new URLSearchParams({
    txn: 'pay',
    recipients: username,
    amount,
  });

  if (note) {
    params.append('note', note);
  }

  url.search = params.toString();

  return url.toString();
}

export const useExternalCheckoutReviewController = () => {
  const {
    formValues,
    initiateCancelProcess,
    toNext,
    context: { isAffiliate, isGroupGifting },
  } = useCheckoutFunnel(({ formValues, initiateCancelProcess, toNext, context }) => [formValues, initiateCancelProcess, toNext, context.isAffiliate, context.isGroupGifting]);
  const { registryItem } = useCheckoutInitDataContext();
  const { buyRegistryItem } = useShoppingCartTelemetry();
  const { externalRegistry } = useGuestRegistryTelemetry();
  const { t } = useTranslation('guestRegistry');
  const { product, productImageSrc, siteName, currency } = useGetProductData('externalCheckoutReview');
  const formatRedirectURL = useFormatRedirectURL();

  const translations = t('checkoutDialog', 'screens', 'externalCheckoutReview');
  const isCharity = registryItem?.donationFund?.fundType === CustomItemDataType.charity;
  const charityTitle = registryItem?.productData.title;
  const platformLabel = isAffiliate ? siteName : isCharity ? charityTitle : formValues?.paymentMethod ? PLATFORM_LABELS[formValues?.paymentMethod] : '';
  const isCashFundType = registryItem?.donationFund?.fundType === 'cash';
  const { customType, eventId, registryItemId, reservedQty, priceValueInMinorUnits, typeOfItem, priceCurrencyCode, productTitle } = useCheckoutTelemetryData();
  const isRetailProduct = isAffiliateWrappableItemType(customType);

  const getRedirectUrl = useCallback(
    (method?: 'contribute' | 'purchase'): string => {
      if (isGroupGifting && method === 'contribute') {
        if (registryItem?.donationFund?.platform?.type === DonationFundPlatformTypeEnum.venmo) {
          return buildVenmoPaymentLink(registryItem?.donationFund?.platform?.username || '', formValues.amount?.toString() || '', formValues.note);
        }

        return registryItem?.donationFund?.platform?.link || '';
      }

      // Synced items hand off to the retailer's registry page (as SyncedProductDialog always has),
      // so the retailer can tie the purchase to the couple's registry and sync it back as purchased.
      const syncedRegistryUrl = registryItem?.externallyOwned ? registryItem.registry?.url : undefined;
      const checkoutUrl = syncedRegistryUrl || registryItem?.productData?.checkoutUrl || '';
      if (isAffiliate && isRetailProduct && checkoutUrl) {
        return formatRedirectURL({ url: checkoutUrl, platform: 'web', eventId });
      }
      return checkoutUrl;
    },
    [registryItem, formValues.amount, formValues.note, isGroupGifting, isAffiliate, isRetailProduct, formatRedirectURL, eventId],
  );

  const handleRedirectToRetailer = useCallback(
    (method?: 'contribute' | 'purchase') => {
      const url = getRedirectUrl(method);
      withWindow(window => {
        window.open(url, '_blank');
      });
      runIfFn(toNext);
    },
    [toNext, getRedirectUrl],
  );

  const handleGoToButton = useCallback(
    (method?: 'contribute' | 'purchase') => {
      const buyRegistryItemData = {
        productTitle,
        registryItemId,
        reservedQty,
        priceValueInMinorUnits,
        priceCurrencyCode,
        itemType: typeOfItem,
        buttonLabel: translations.goToButton({ platform: platformLabel }),
        redirectUrl: registryItem?.productData.externalUrl,
        action: 'redirect',
        method: method || 'purchase',
        isGroupGiftingEnabled: isGroupGifting,
      };
      buyRegistryItem(buyRegistryItemData);
      addAction('buyRegistryItem', buyRegistryItemData);
      if (registryItem?.externallyOwned && registryItem.registry?.id) {
        // parity with SyncedProductDialog, which reported synced handoffs as ExternalRegistry clicks
        externalRegistry({
          id: registryItem.registry.id,
          priceValueInMinorUnits,
          // the product's currency, not the checkout telemetry default (USD unless the item is a cash fund)
          priceCurrencyCode: getCurrencySymbolFromCurrencyCode(currency),
          reservedQty,
          destinationUrl: registryItem.registry.url ?? undefined,
          typeOfItem,
        });
      }
      handleRedirectToRetailer(method);
    },
    [
      buyRegistryItem,
      currency,
      externalRegistry,
      isGroupGifting,
      platformLabel,
      priceCurrencyCode,
      priceValueInMinorUnits,
      productTitle,
      handleRedirectToRetailer,
      registryItem?.externallyOwned,
      registryItem?.registry,
      registryItem?.productData.externalUrl,
      registryItemId,
      reservedQty,
      translations,
      typeOfItem,
    ],
  );

  return {
    formValues,
    initiateCancelProcess,
    redirectToExternalGateway: handleGoToButton,
    translations,
    platformLabel,
    isCharity,
    isAffiliate,
    product,
    currency,
    productImageSrc,
    isCashFundType,
  };
};
